import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowForward as ContinueIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { LoadingSpinner, ConfirmDialog } from '../components/common';
import { quotesAPI } from '../api';
import { downloadTransactionExcel } from '../utils/fileDownload';
import { formatCurrency } from '../utils/calculations';
import { TRANSACTION_TYPES, createEmptyLineItem } from '../constants';

// Maps a saved quote's line items (each with a nested `product` relation)
// into the shape TransactionForm/LineItemsTable expect - same fields as
// createEmptyLineItem(), with a fresh _rowId so React can track row identity
// once these land in the destination form's state.
const mapQuoteItemsToLineItems = (quoteItems = []) =>
  quoteItems.map((qi) => ({
    ...createEmptyLineItem(),
    registration: qi.registration || '',
    part_number: qi.product?.part_number || qi.part_number || '',
    description: qi.product?.description || qi.description || '',
    qty: qi.qty !== undefined && qi.qty !== null ? parseFloat(qi.qty) : 0,
    price: qi.price !== undefined && qi.price !== null ? parseFloat(qi.price) : 0,
    discount: qi.discount !== undefined && qi.discount !== null ? parseFloat(qi.discount) : 0,
    vat_perc: qi.vat_perc !== undefined && qi.vat_perc !== null ? qi.vat_perc : 23,
    subtotal: qi.subtotal !== undefined && qi.subtotal !== null ? parseFloat(qi.subtotal) : 0,
    vat: qi.vat !== undefined && qi.vat !== null ? parseFloat(qi.vat) : 0,
    total: qi.total !== undefined && qi.total !== null ? parseFloat(qi.total) : 0,
  }));

export default function Quotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Both filters are applied client-side against the already-loaded list
  // rather than round-tripping to the API - quotes.getAll() already returns
  // everything (including items.product), and this list isn't expected to
  // grow large enough that client-side filtering becomes a problem.
  const [searchText, setSearchText] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await quotesAPI.getAll();
      setQuotes(response.data || []);
    } catch (error) {
      console.error('Failed to load quotes:', error);
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  const handleContinue = useCallback((quote) => {
    const prefillState = {
      sourceQuoteId: quote.id,
      customerName: quote.customer?.customer || '',
      account: quote.account || null,
      orderNumber: quote.order_number || '',
      items: mapQuoteItemsToLineItems(quote.items),
      vatExempt: false,
    };

    if (quote.accounts_id) {
      navigate('/account-invoice', { state: prefillState });
    } else {
      navigate('/cash-sale', { state: prefillState });
    }
  }, [navigate]);

  const handleDownload = useCallback(async (quote) => {
    try {
      const response = await quotesAPI.downloadPDF(quote.id);
      downloadTransactionExcel(response, TRANSACTION_TYPES.QUOTE, quote.id);
      toast.success('Download complete');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download quote');
    }
  }, []);

  const filteredQuotes = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    const customer = customerFilter.trim().toLowerCase();

    return quotes.filter((quote) => {
      const matchesSearch = !search || (quote.items || []).some((item) => {
        const partNumber = (item.product?.part_number || item.part_number || '').toLowerCase();
        const registration = (item.registration || '').toLowerCase();
        return partNumber.includes(search) || registration.includes(search);
      });

      const customerOrAccountName = (quote.customer?.customer || quote.account?.name || '').toLowerCase();
      const matchesCustomer = !customer || customerOrAccountName.includes(customer);

      return matchesSearch && matchesCustomer;
    });
  }, [quotes, searchText, customerFilter]);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await quotesAPI.delete(deleteTarget.id);
      toast.success('Quote deleted');
      setDeleteTarget(null);
      loadQuotes();
    } catch (error) {
      console.error('Delete quote error:', error);
      toast.error('Failed to delete quote');
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'Quote #',
        cell: info => `Quote # ${info.getValue()}`,
        size: 120
      },
      {
        id: 'customerOrAccount',
        header: 'Customer/Account',
        cell: ({ row }) => row.original.customer?.customer || row.original.account?.name || '-',
        size: 200
      },
      {
        accessorKey: 'order_number',
        header: 'Order No.',
        cell: info => info.getValue() || '-',
        size: 120
      },
      {
        accessorKey: 'timestamp',
        header: 'Date',
        cell: info => {
          try {
            return format(new Date(info.getValue()), 'dd/MM/yyyy HH:mm:ss');
          } catch (e) {
            return '-';
          }
        },
        size: 160
      },
      {
        accessorKey: 'valid_until',
        header: 'Valid Until',
        // Expiry is informational only - it never disables Continue/Download/
        // Delete, it's just a signal to whoever's looking at the list.
        cell: info => {
          const value = info.getValue();
          if (!value) return '-';
          const isExpired = value < Date.now();
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{format(new Date(value), 'dd/MM/yyyy')}</Typography>
              <Chip
                label={isExpired ? 'Expired' : 'Valid'}
                color={isExpired ? 'error' : 'success'}
                size="small"
              />
            </Box>
          );
        },
        size: 200
      },
      {
        accessorKey: 'total',
        header: 'Total',
        cell: info => formatCurrency(info.getValue()),
        size: 100
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              color="primary"
              title="Continue"
              onClick={() => handleContinue(row.original)}
            >
              <ContinueIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="primary"
              title="Download"
              onClick={() => handleDownload(row.original)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              title="Delete"
              onClick={() => setDeleteTarget(row.original)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
        size: 150
      }
    ],
    [handleContinue, handleDownload]
  );

  const table = useReactTable({
    data: filteredQuotes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Quotes</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/quotes/new')}
        >
          New Quote
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Search by Part No. or Registration"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Filter by Customer/Account"
              placeholder="Customer or account name"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {quotes.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No quotes found. Create your first quote to get started.
          </Typography>
        </Paper>
      ) : filteredQuotes.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No quotes match your search.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableCell key={header.id} sx={{ fontWeight: 'bold' }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map(row => (
                <TableRow key={row.id} hover>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Quote"
        message={`Are you sure you want to delete Quote # ${deleteTarget?.id}? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
}
